import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { itemVisualDensity, productTone, readNumberMeta, readStringMeta, visibleLaneItems } from "../product/rendering";

function severityTone(severity: string | null) {
  switch (severity) {
    case "critical":
      return "border-rose-700/16 bg-rose-50/92 text-rose-900";
    case "warning":
      return "border-amber-700/16 bg-amber-50/92 text-amber-900";
    default:
      return "border-emerald-700/16 bg-emerald-50/92 text-emerald-900";
  }
}

function stateChip(label: string, className: string) {
  return <span className={["rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]", className].join(" ")}>{label}</span>;
}

export default function OverlayLaneRenderer({ lane, items, viewport }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);

  return (
    <div className="relative h-full min-h-[48px]" data-lane-class="overlay" data-lane-id={lane.id} data-lane-items={items.length}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-amber-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const label = item.itemType === "overlay" ? item.overlayKey : item.id;
          const severity = item.itemType === "overlay" ? item.severity ?? null : null;
          const confidence = item.itemType === "overlay" ? item.confidence ?? readNumberMeta(item, "confidence") : readNumberMeta(item, "confidence");
          const status = readStringMeta(item, "status") ?? readStringMeta(item, "mode");

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-amber-300/26 bg-[linear-gradient(135deg,rgba(251,191,36,0.16),rgba(120,53,15,0.12),rgba(2,6,23,0.20))] px-3 shadow-[0_8px_20px_rgba(245,158,11,0.10)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.10),rgba(255,255,255,0.02),rgba(255,255,255,0.10))]" />
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {density !== "micro" ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-amber-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {severity ? stateChip(severity, severityTone(severity)) : null}
                        {typeof confidence === "number" ? stateChip(`${Math.round(confidence * 100)}%`, "border-slate-700/16 bg-slate-50/92 text-slate-900") : null}
                        {status ? stateChip(status, "border-amber-700/16 bg-amber-50/92 text-amber-900") : null}
                      </div>
                    </>
                  ) : (
                    <div className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-50/88">Ovr</div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="grid h-full place-items-center px-3">
          <div className={["rounded-full border px-3 py-1 text-[10px] font-semibold", tone.emptyClassName].join(" ")}>No overlays in view</div>
        </div>
      )}
    </div>
  );
}